import { useCallback, useEffect, useRef } from 'react';

/**
 * 編集中フォームの内容を sessionStorage に自動退避し、マウント時に復元するフック。
 *
 * 想定する事故シナリオ:
 * - refresh token が失効 → PrivateRoute が即 Navigate → コンポーネント unmount で React state が消失
 * - ユーザーがリロード or 戻るで一時的に画面を離脱
 * - 他タブからのログアウト伝搬（2026-05-31 マルチタブ UX 対応）
 *
 * sessionStorage を採用しているため Issue #23 のタブ単位独立性は維持する。
 * タブを閉じると草稿も消えるが、ユーザーが明示的に閉じた場合の挙動なので許容範囲とする。
 *
 * 使い方:
 *   const { clearDraft } = useFormDraft('admin/prompt-edit/abc-123', { title, content, reason }, (d) => {
 *     setTitle(d.title);
 *     setContent(d.content);
 *     setReason(d.reason);
 *   });
 *   // 保存成功後に clearDraft() を呼ぶ
 *
 * @param key 草稿の識別キー（例: ページ + リソース ID）
 * @param value 監視対象のフォーム値（オブジェクト or プリミティブ）
 * @param restore マウント時に sessionStorage に有効な草稿があった場合の復元ハンドラ
 * @param options.ttlMs 草稿の有効期限（デフォルト 24 時間）
 * @param options.enabled false の場合、保存も復元も行わない（初期ロード未完了などで使う）
 */
export function useFormDraft<T>(
  key: string,
  value: T,
  restore: (draft: T) => void,
  options?: { ttlMs?: number; enabled?: boolean }
): { clearDraft: () => void } {
  const fullKey = `ceeu_draft_${key}`;
  const ttlMs = options?.ttlMs ?? 24 * 60 * 60 * 1000;
  const enabled = options?.enabled !== false;
  const restoredRef = useRef(false);
  const restoreRef = useRef(restore);
  restoreRef.current = restore;

  // マウント時に一度だけ復元
  useEffect(() => {
    if (!enabled || restoredRef.current) return;
    restoredRef.current = true;
    try {
      const raw = sessionStorage.getItem(fullKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { ts: number; value: T };
      if (typeof parsed.ts !== 'number' || Date.now() - parsed.ts > ttlMs) {
        sessionStorage.removeItem(fullKey);
        return;
      }
      restoreRef.current(parsed.value);
    } catch {
      // 壊れた草稿は捨てる
      try {
        sessionStorage.removeItem(fullKey);
      } catch {
        /* ignore */
      }
    }
  }, [fullKey, enabled, ttlMs]);

  // 値の変化をデバウンスで保存（500ms）
  useEffect(() => {
    if (!enabled || !restoredRef.current) return;
    const timer = setTimeout(() => {
      try {
        sessionStorage.setItem(fullKey, JSON.stringify({ ts: Date.now(), value }));
      } catch {
        /* quota etc. — silently fail */
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [value, fullKey, enabled]);

  const clearDraft = useCallback(() => {
    try {
      sessionStorage.removeItem(fullKey);
    } catch {
      /* ignore */
    }
  }, [fullKey]);

  return { clearDraft };
}

/**
 * ページ離脱時の確認ダイアログ（beforeunload 警告）。
 * リロードやタブ閉じの誤操作を防ぐ。
 *
 * @param isDirty true の間、ブラウザ標準の離脱確認ダイアログを出す
 */
export function useBeforeUnloadWarning(isDirty: boolean): void {
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // 大半のブラウザは独自メッセージを無視し標準ダイアログを出す
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);
}
